import { Body1 } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import styled from 'styled-components'
import Commitment from 'assets/about-safety/commitment.webp'
import Prevention from 'assets/about-safety/prevention.webp'
import Moderation from 'assets/about-safety/moderation.webp'
import Escalation from 'assets/about-safety/escalation.webp'
import Guidelines from 'assets/about-safety/guidelines.webp'
import SecurityIcon from 'assets/about-safety/security_banner_asset.webp'
import Support from 'assets/about-safety/support.webp'
import Checkbox from 'assets/about-safety/check.svg'
import { parseLocale } from 'lib/constants/localization'
import { GetStaticProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import _useTranslation from 'lib/hooks/_useTranslation'
import SmallFeatureGrid from 'stories/elements/Components/SmallFeatureGrid'
import { useEffect } from 'react'
import { onPageEnter } from 'lib/tracking/pageEnter'
import Meta from 'components/modules/SEO/Meta'
import { NewButtonA, NewButtonLink } from 'components/elements/NewButton'
import { legalGuidelinesRoute } from 'lib/constants/routes'
import { getSupportPageLinks } from 'lib/constants/support'
import NextImage from 'components/elements/NextImage'

const Container = styled.div`
    section {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 50px;
    }
    .cards {
        display: flex;
        flex-wrap: wrap;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        gap: 25px;
    }
    .elements {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 75px;
    }
    .button-row {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        @media (max-width: 750px) {
            * {
                width: 100%;
            }
        }
    }
    .banner {
        width: 85%;
        height: fit-content;
        padding: 25px;
        border-radius: ${theme.borderRadius.content};
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: #dce2ff;
        border: 1px solid #e7f0f6;
        @media (max-width: 1050px) {
            img {
                width: 70px;
                height: 70px;
            }
        }
    }
`

const Card = styled.div`
    display: flex;

    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 15px;
    width: 250px;
    height: 100px;
    background-color: #2944bc;
    border-radius: ${theme.borderRadius.normal};
    p {
        font-family: var(--font-poppins);
        font-size: 16px;
        line-height: 24px;
        font-weight: 600;
        color: ${theme.colors.white};
    }
    @media (max-width: 750px) {
        width: 100%;
    }
`

const Safety = () => {
    const { t } = _useTranslation('safety')

    useEffect(() => {
        onPageEnter()
    }, [])

    const elements = [
        {
            headline: t('safety/communityGuidelines'),
            subtitle: t('safety/whatAreCommunityGuidelines'),
            text: t('safety/ourCommunityGuidelines'),
            image: Guidelines,
            children: (
                <NewButtonLink
                    href={legalGuidelinesRoute}
                    color={theme.colors.darkBlue}
                    borderRadius={theme.borderRadius.normal}
                    width={350}
                >
                    <Body1 textAlign="center" fontFamily="var(--font-inter)" fontWeight={500} color={theme.colors.white}>
                        {t('safety/viewCommunityGuidelines')}
                    </Body1>
                </NewButtonLink>
            ),
        },
        {
            headline: t('safety/moderation'),
            subtitle: t('safety/whatIsModeration'),
            text: t('safety/ourModeration'),
            image: Moderation,
            isInverted: true,
        },
        {
            headline: t('safety/prevention'),
            subtitle: t('safety/whatIsPrevention'),
            text: t('safety/ourPrevention'),
            image: Prevention,
        },
        {
            headline: t('safety/escalation'),
            subtitle: t('safety/whatIsEscalation'),
            text: t('safety/ourEscalation'),
            image: Escalation,
            isInverted: true,
        },
        {
            headline: t('safety/supportTeam'),
            subtitle: t('safety/howCanKnowunitySupportTeamHelp'),
            text: t('safety/ourSupportTeam'),
            image: Support,
            children: (
                <div className="button-row">
                    <NewButtonA
                        href={getSupportPageLinks()}
                        target="_blank"
                        color={theme.colors.darkBlue}
                        borderRadius={theme.borderRadius.normal}
                        width={275}
                    >
                        <Body1 fontFamily="var(--font-inter)" fontWeight={500} color={theme.colors.white}>
                            {t('safety/contactSupport')}
                        </Body1>
                    </NewButtonA>
                    <NewButtonA
                        href={getSupportPageLinks({ showArticles: true })}
                        target="_blank"
                        color={theme.colors.darkBlue}
                        borderRadius={theme.borderRadius.normal}
                        width={300}
                        type="secondary"
                    >
                        <Body1 fontFamily="var(--font-inter)" fontWeight={500} color={theme.colors.darkBlue}>
                            {t('safety/viewSupportArticles')}
                        </Body1>
                    </NewButtonA>
                </div>
            ),
        },
    ]

    return (
        <Container>
            <Meta title={t('safety/knowunityIsCommittedToKeepingStudentsSafe')} />
            <section>
                <div className="banner">
                    <NextImage
                        src={SecurityIcon.src}
                        width={SecurityIcon.width}
                        height={SecurityIcon.height}
                        alt="safety icon"
                    />
                </div>
                <SmallFeatureGrid
                    isHeader
                    headline={t('safety/knowunityIsCommittedToKeepingStudentsSafe')}
                    subtitle={t('safety/howDoWeDoThat')}
                    text={t('safety/knowunityIsBuiltWithStudentsNeedsInMind')}
                    image={Commitment}
                />

                <div className="cards">
                    <Card>
                        <Checkbox />
                        <Body1>{t('safety/moderation')}</Body1>
                    </Card>
                    <Card>
                        <Checkbox />
                        <Body1>{t('safety/prevention')}</Body1>
                    </Card>
                    <Card>
                        <Checkbox />
                        <Body1>{t('safety/escalation')}</Body1>
                    </Card>
                </div>
                <div className="elements">
                    {elements.map((element) => (
                        <SmallFeatureGrid key={element.headline} {...element} />
                    ))}
                </div>
            </section>
        </Container>
    )
}

export default Safety

export const getStaticProps: GetStaticProps = async (props) => ({
    props: {
        ...(await serverSideTranslations(parseLocale(props.locale).interfaceLanguageCode, ['common', 'safety'])),
    },
})
